<template>
  <BModal
    :modal-name="modalName"
    width="400px"
    height="auto"
  >
    <BModalHeader
      :title="$t('productCategoryAssociation.addTitle')"
      @modal-close="handleClose"
    />
    <form
      class="modal-body"
      @submit.prevent="handleAdd"
    >
      <div class="form-group">
        <label
          for="product-category-select"
          class="form-label"
        >{{ $t('productCategory.title') }}</label>
        <ElSelect
          id="product-category-select"
          v-model="selectedCategoryId"
          :placeholder="$t('productCategory.title')"
          class="full-width"
        >
          <ElOption
            v-for="category in productCategories"
            :key="category.id"
            :label="category.name"
            :value="category.id"
            class="option-select"
          />
        </ElSelect>
      </div>
      <div class="form-group">
        <label
          for="product-category-status-select"
          class="form-label"
        >{{ $t('productCategoryAssociation.status') }}</label>
        <ElSelect
          id="product-category-status-select"
          v-model="selectedStatusId"
          :placeholder="$t('productCategoryAssociation.status')"
          class="full-width"
        >
          <ElOption
            v-for="status in productCategoryStatuses"
            :key="status.id"
            :label="status.name"
            :value="status.id"
            class="option-select"
          />
        </ElSelect>
      </div>
      <div class="form-actions">
        <BBtn
          type="primary"
          btn-type="submit"
          :disabled="!isFormValid"
          :loading="submitting"
        >
          {{ $t('productCategoryAssociation.add') }}
        </BBtn>
      </div>
    </form>
  </BModal>
</template>

<script lang="ts" setup>
import { ref, computed, inject } from 'vue';
import type { ProductCategory, ProductCategoryStatus } from '@/api/openapi';
import { KEY_OF_MODAL } from '@/injection-keys';

const props = defineProps<{
  modalName: string;
  productCategories: ProductCategory[];
  productCategoryStatuses: ProductCategoryStatus[];
  submitting: boolean;
}>();

const emit = defineEmits<{
  add: [productCategoryId: number, productCategoryStatusId: number];
}>();

const modal = inject(KEY_OF_MODAL);

const selectedCategoryId = ref<number | null>(null);
const selectedStatusId = ref<number | null>(null);

const isFormValid = computed(() => selectedCategoryId.value != null && selectedStatusId.value != null);

const resetForm = () => {
  selectedCategoryId.value = null;
  selectedStatusId.value = null;
};

const handleClose = () => {
  resetForm();
  modal.hide(props.modalName);
};

const handleAdd = () => {
  if (!isFormValid.value || props.submitting) return;
  emit('add', selectedCategoryId.value, selectedStatusId.value);
};

defineExpose({ resetForm, handleClose });
</script>

<style lang="scss" scoped>
.modal-body {
  padding: $basespace-400;
}

.form-group {
  margin-bottom: $basespace-400;
}

.form-label {
  display: block;
  font-size: $fontsize-100;
  color: $textcolor-light;
  margin-bottom: $basespace-100;
}

.full-width {
  width: 100%;
}

.form-actions {
  display: flex;
  justify-content: center;
  margin-top: $basespace-400;
}

.option-select {
  padding: 0 $basespace-100;
}
</style>
